/**
 * Step 2 do NewTenantWizard — Estrutura (TASK-2435).
 *
 * Coleta:
 *   - direct_environments: Checkbox.Group com DEV/QA/HML/PRD
 *   - is_saas: Switch
 *   - sub_tenants[]: lista dinâmica (apenas quando is_saas=true)
 *
 * Regras espelhadas do TenantSpec (Pydantic):
 *   - is_saas=True exige >=1 sub_tenant
 *   - sub_tenants exige is_saas=True
 *   - precisa de >=1 ambiente direto OU >=1 sub_tenant
 */

import {
  Stack,
  Checkbox,
  Switch,
  Text,
  Card,
  Group,
  Button,
  TextInput,
  ActionIcon,
  Divider,
  Alert,
  Box,
} from '@mantine/core'
import { IconPlus, IconTrash, IconInfoCircle } from '@tabler/icons-react'
import {
  ENV_OPTIONS,
  PROJECT_NAME_REGEX,
  type EnvName,
  type SubTenantSpec,
  type TenantSpec,
} from '@/api/awsOrgProvisioning'

interface WizardStepEstruturaProps {
  spec: TenantSpec
  onChange: (spec: TenantSpec) => void
}

export function WizardStepEstrutura({
  spec,
  onChange,
}: WizardStepEstruturaProps) {
  const setSpec = (patch: Partial<TenantSpec>) => onChange({ ...spec, ...patch })

  const handleEnvChange = (values: string[]) =>
    setSpec({ direct_environments: values as EnvName[] })

  const handleToggleSaas = (checked: boolean) => {
    if (checked) {
      // Ao ligar SaaS, garante que existe pelo menos 1 sub-tenant placeholder
      setSpec({
        is_saas: true,
        sub_tenants:
          spec.sub_tenants.length > 0
            ? spec.sub_tenants
            : [{ name: '', environments: [] }],
      })
    } else {
      // Ao desligar SaaS, limpa sub_tenants (regra do Pydantic)
      setSpec({ is_saas: false, sub_tenants: [] })
    }
  }

  const handleAddSubTenant = () => {
    setSpec({
      sub_tenants: [...spec.sub_tenants, { name: '', environments: [] }],
    })
  }

  const handleRemoveSubTenant = (idx: number) => {
    setSpec({
      sub_tenants: spec.sub_tenants.filter((_, i) => i !== idx),
    })
  }

  const handleUpdateSubTenant = (idx: number, patch: Partial<SubTenantSpec>) => {
    setSpec({
      sub_tenants: spec.sub_tenants.map((st, i) =>
        i === idx ? { ...st, ...patch } : st,
      ),
    })
  }

  return (
    <Stack gap="lg" mt="md">
      <Alert
        variant="light"
        color="blue"
        icon={<IconInfoCircle size={18} />}
        title="Estrutura de ambientes"
      >
        <Text size="sm">
          Ambientes diretos ficam abaixo do OU principal. Ative o modo{' '}
          <b>SaaS</b> se o tenant precisa hospedar múltiplos clientes (cada
          sub-tenant ganha seu próprio bloco de ambientes).
        </Text>
      </Alert>

      <Box>
        <Text size="sm" fw={500} mb={4}>
          Ambientes diretos
        </Text>
        <Text size="xs" c="dimmed" mb="xs">
          Selecione os ambientes que serão criados diretamente sob{' '}
          {spec.project_name || 'o projeto'}.
        </Text>
        <Checkbox.Group
          value={spec.direct_environments}
          onChange={handleEnvChange}
        >
          <Group gap="lg">
            {ENV_OPTIONS.map((env) => (
              <Checkbox
                key={env}
                value={env}
                label={env}
                data-testid={`wizard-env-direct-${env}`}
              />
            ))}
          </Group>
        </Checkbox.Group>
      </Box>

      <Divider />

      <Switch
        label="Tenant SaaS (com sub-tenants)"
        description="Ative se este tenant hospeda múltiplos clientes finais."
        checked={spec.is_saas}
        onChange={(e) => handleToggleSaas(e.currentTarget.checked)}
        data-testid="wizard-saas-switch"
      />

      {spec.is_saas && (
        <Stack gap="sm">
          <Group justify="space-between" align="center">
            <Text size="sm" fw={500}>
              Sub-tenants ({spec.sub_tenants.length})
            </Text>
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPlus size={14} />}
              onClick={handleAddSubTenant}
              data-testid="wizard-add-subtenant"
            >
              Adicionar sub-tenant
            </Button>
          </Group>

          {spec.sub_tenants.length === 0 && (
            <Text size="sm" c="dimmed">
              Nenhum sub-tenant ainda. Clique em "Adicionar sub-tenant" para
              criar o primeiro.
            </Text>
          )}

          {spec.sub_tenants.map((st, idx) => (
            <SubTenantCard
              key={idx}
              index={idx}
              subTenant={st}
              onUpdate={(patch) => handleUpdateSubTenant(idx, patch)}
              onRemove={() => handleRemoveSubTenant(idx)}
            />
          ))}
        </Stack>
      )}
    </Stack>
  )
}

interface SubTenantCardProps {
  index: number
  subTenant: SubTenantSpec
  onUpdate: (patch: Partial<SubTenantSpec>) => void
  onRemove: () => void
}

function SubTenantCard({
  index,
  subTenant,
  onUpdate,
  onRemove,
}: SubTenantCardProps) {
  const nameTouched = subTenant.name.length > 0
  const nameError =
    nameTouched && !PROJECT_NAME_REGEX.test(subTenant.name)
      ? 'Use apenas A-Z e 0-9, comece com letra.'
      : null

  return (
    <Card withBorder p="md" radius="sm">
      <Stack gap="sm">
        <Group justify="space-between">
          <Text size="sm" fw={500}>
            Sub-tenant #{index + 1}
          </Text>
          <ActionIcon
            color="red"
            variant="subtle"
            onClick={onRemove}
            aria-label={`Remover sub-tenant ${index + 1}`}
            data-testid={`wizard-remove-subtenant-${index}`}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Group>

        <TextInput
          label="Nome"
          placeholder="GETNET"
          required
          value={subTenant.name}
          error={nameError}
          onChange={(e) =>
            onUpdate({ name: e.currentTarget.value.toUpperCase() })
          }
          data-testid={`wizard-subtenant-name-${index}`}
        />

        <Box>
          <Text size="sm" fw={500} mb={4}>
            Ambientes
          </Text>
          <Checkbox.Group
            value={subTenant.environments}
            onChange={(values) =>
              onUpdate({ environments: values as EnvName[] })
            }
          >
            <Group gap="lg">
              {ENV_OPTIONS.map((env) => (
                <Checkbox
                  key={env}
                  value={env}
                  label={env}
                  data-testid={`wizard-subtenant-${index}-env-${env}`}
                />
              ))}
            </Group>
          </Checkbox.Group>
        </Box>
      </Stack>
    </Card>
  )
}
